/*
 * myPokemonShiny.ts — is the Pokémon on this caught-Pokémon page SHINY?
 *
 * The signal is colour: a shiny is the same model in a different palette.
 * Until 2026-09-18 this compared an 8-bin hue histogram of the whole crop to
 * the two references' histograms, and it had two structural faults on device
 * frames: the page scene behind the model (a lucky gold bokeh, the Shadow
 * storm sky, a habitat swirl) is most of the crop and only ONE colour of it
 * was subtracted, and a hue bin cannot tell a shade from its neighbour, so
 * 358 of 1025 species were refused as "too similar" before a pixel was read
 * (Charcadet and Nacli scored identical on hue; a shiny Dialga under lucky
 * sparkles lost by 0.01).
 *
 * Now the compare is on PALETTES (spriteFingerprintCore.extractPalette, the
 * same routine the generator ran on the reference art):
 *
 *   1. the crop's border ring gives the SCENE's palette, and every pixel
 *      within reach of a scene colour is ignored (the bokeh, the sky, the
 *      swirl are many shades, so a palette, not one median);
 *   2. each remaining pixel is matched to the nearest reference colour; a
 *      pixel near a colour that belongs to ONLY the shiny palette votes shiny,
 *      near a base-only colour votes base, near a shared colour is neutral,
 *      near neither is scene or shadow and is skipped;
 *   3. the verdict is the vote ratio with a margin, on enough votes.
 *
 * Still three-valued: true / false / null (the two palettes share every
 * colour, the crop matches neither, or the votes are too close). A false
 * never clears a record's flag; the store treats shiny as sticky-true.
 *
 * Cost: one pass over the crop at stride 3 against at most 16 colours, a few
 * milliseconds on the phone, on the probe frame the OCR already paid for.
 */
import palettes from "./generated/spritePalettes.generated.json";
import {
  colourDistance,
  extractPalette,
  PALETTE_MERGE_DISTANCE,
  type PaletteColour,
} from "./spriteFingerprintCore";

interface PalettePair {
  base?: PaletteColour[] | null;
  shiny?: PaletteColour[] | null;
}
const LIBRARY = palettes as unknown as Record<string, PalettePair>;

/** A reference colour this close to one in the other palette is SHARED. */
const SHARED_DISTANCE = 40;
/** A crop pixel this close to a reference colour is that colour. */
const MATCH_DISTANCE = 44;
/** A crop pixel this close to a scene colour is the scene. */
const SCENE_DISTANCE = 36;
/** Votes needed, as a share of the sampled crop, before a verdict. */
const MIN_VOTE_FRAC = 0.01;
/** The winning palette's share of the votes. */
const MIN_WIN_RATIO = 0.62;
/** Border ring width as a share of the crop's shorter side. */
const RING_FRAC = 0.06;
const STRIDE = 3;
/**
 * A reference colour with less chroma than this (max channel minus min
 * channel) is NEUTRAL and never decides (2026-09-28): creams, greys and
 * whites appear on any model as shading, an egg, a highlight, and on any
 * backdrop. A plain Chansey was read shiny because its reference shiny
 * palette (the pale green one) carries cream and khaki entries that the
 * pink model's egg shading and a warm autumn scene matched pixel for pixel.
 */
const NEUTRAL_CHROMA = 26;
/** A crop pixel with less chroma than this cannot vote for the same reason. */
const PIXEL_MIN_CHROMA = 20;

function chroma(r: number, g: number, b: number): number {
  return Math.max(r, g, b) - Math.min(r, g, b);
}

export interface ShinyVerdict {
  /** true = shiny palette, false = base palette, null = cannot tell. */
  shiny: boolean | null;
  /** Why, for the device log. */
  reason:
    | "shiny"
    | "base"
    | "no_reference"
    | "references_too_similar"
    | "no_resemblance"
    | "margin_too_thin";
  /** Diagnostics: votes for each palette and the share sampled. */
  shinyVotes?: number;
  baseVotes?: number;
  /** Diagnostics (2026-09-29): the weighted share and the plain count share that were shiny. */
  weightedRatio?: number;
  countRatio?: number;
}

/** Colours of `own` with no colour of `other` within SHARED_DISTANCE. */
function distinctColours(own: PaletteColour[], other: PaletteColour[]): PaletteColour[] {
  return own.filter(
    (c) => !other.some((o) => colourDistance(c[0], c[1], c[2], o[0], o[1], o[2]) <= SHARED_DISTANCE),
  );
}

/** Classify a caught-page sprite crop against the species' two palettes. */
export function classifyShinySprite(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  speciesId: number,
): ShinyVerdict {
  const refs = LIBRARY[`__dex__:${speciesId}`];
  const base = refs?.base ?? null;
  const shiny = refs?.shiny ?? null;
  if (!base || !shiny || base.length === 0 || shiny.length === 0) {
    return { shiny: null, reason: "no_reference" };
  }
  const shinyOnly = distinctColours(shiny, base).filter((c) => chroma(c[0], c[1], c[2]) >= NEUTRAL_CHROMA);
  const baseOnly = distinctColours(base, shiny).filter((c) => chroma(c[0], c[1], c[2]) >= NEUTRAL_CHROMA);
  if (shinyOnly.length === 0 && baseOnly.length === 0) {
    return { shiny: null, reason: "references_too_similar" };
  }
  if (width < 20 || height < 20) return { shiny: null, reason: "no_resemblance" };
  // The scene: the crop's border ring.
  const ring = Math.max(1, Math.floor(Math.min(width, height) * RING_FRAC));
  const scene = extractPalette(data, width, height, {
    k: 6,
    stride: 2,
    within: (x, y) => x < ring || x >= width - ring || y < ring || y >= height - ring,
  });
  // Every reference colour tagged with its vote. A vote carries the
  // colour's share of its own palette (2026-09-28): a pixel of the body
  // colour that covers 44% of the model outweighs one of a 2% trim colour,
  // so an outline, a highlight or a leaked backdrop cluster cannot outvote
  // the body. Votes used to be unweighted pixel counts.
  const refsAll: Array<{ c: PaletteColour; vote: 1 | -1 | 0; weight: number }> = [
    ...shinyOnly.map((c) => ({ c, vote: 1 as const, weight: Math.max(0.02, c[3]) })),
    ...baseOnly.map((c) => ({ c, vote: -1 as const, weight: Math.max(0.02, c[3]) })),
    ...shiny.filter((c) => !shinyOnly.includes(c)).map((c) => ({ c, vote: 0 as const, weight: 0 })),
    ...base.filter((c) => !baseOnly.includes(c)).map((c) => ({ c, vote: 0 as const, weight: 0 })),
  ];
  let shinyVotes = 0;
  let baseVotes = 0;
  let shinyCount = 0;
  let baseCount = 0;
  let sampled = 0;
  for (let y = 0; y < height; y += STRIDE) {
    for (let x = 0; x < width; x += STRIDE) {
      const i = (y * width + x) * 4;
      if (data[i + 3] < 128) continue;
      sampled += 1;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      let isScene = false;
      for (let k = 0; k < scene.length; k++) {
        const s = scene[k];
        if (colourDistance(r, g, b, s[0], s[1], s[2]) <= SCENE_DISTANCE) { isScene = true; break; }
      }
      if (isScene) continue;
      if (chroma(r, g, b) < PIXEL_MIN_CHROMA) continue;
      let bestD = Infinity;
      let bestVote: 1 | -1 | 0 = 0;
      let bestWeight = 0;
      for (let k = 0; k < refsAll.length; k++) {
        const c = refsAll[k].c;
        const d = colourDistance(r, g, b, c[0], c[1], c[2]);
        if (d < bestD) { bestD = d; bestVote = refsAll[k].vote; bestWeight = refsAll[k].weight; }
      }
      if (bestD > MATCH_DISTANCE) continue;
      if (bestVote === 1) { shinyVotes += bestWeight; shinyCount += 1; }
      else if (bestVote === -1) { baseVotes += bestWeight; baseCount += 1; }
    }
  }
  const count = shinyCount + baseCount;
  if (sampled === 0 || count < sampled * MIN_VOTE_FRAC) {
    return { shiny: null, reason: "no_resemblance", shinyVotes: shinyCount, baseVotes: baseCount };
  }
  const ratio = shinyVotes / (shinyVotes + baseVotes);
  // BOTH readings must agree (2026-09-29 owner frame: a plain Excadrill read
  // shiny). Its dark body is neutral and cannot vote, its red markings sit
  // nearer the shiny's maroon body than the base's own red, and that body
  // colour carries 42% of the shiny palette's weight, so a handful of
  // marking pixels outweighed an equal number of base pixels. The weighted
  // ratio says which palette the matched pixels RESEMBLE; the raw count
  // says which palette MORE of them belong to. A verdict needs a weighted
  // margin and a plain majority in the same direction.
  const countRatio = shinyCount / count;
  const diag = { shinyVotes: shinyCount, baseVotes: baseCount, weightedRatio: ratio, countRatio };
  if (ratio >= MIN_WIN_RATIO && countRatio > 0.5) return { shiny: true, reason: "shiny", ...diag };
  if (ratio <= 1 - MIN_WIN_RATIO && countRatio < 0.5) return { shiny: false, reason: "base", ...diag };
  return { shiny: null, reason: "margin_too_thin", ...diag };
}

/** Test seam: the merge distance the palettes were built with. */
export const __PALETTE_MERGE_DISTANCE = PALETTE_MERGE_DISTANCE;
